import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { 
  AlertTriangle, 
  Zap,
  TrendingUp,
  Clock,
  FileText,
  RefreshCw,
  Download,
  Filter,
  Search,
  X,
  ChevronDown,
  Calendar
} from 'lucide-react';
import { ticketService } from '../api/ticketService';
import { usePriorities } from '../hooks/usePriorities';
import { useStatuses } from '../hooks/useStatuses';
import EscalatedTicketsTable from '../components/EscalatedTicketsTable';
import { toast } from 'react-hot-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { extractErrorMessage } from '../utils/errorHandler';
import {
  resolveTicketCategoryLabel,
  resolveTicketPriorityLabel,
  resolveCurrentSlaWindow,
} from '../utils/inquiryDisplay';

const EscalatedTicketsManagement = () => {
  const [escalationStats, setEscalationStats] = useState({
    totalEscalated: 0,
    criticalEscalated: 0,
    highEscalated: 0,
    mediumEscalated: 0,
    lowEscalated: 0,
    avgEscalationTime: '0h 0m',
    escalationRate: 0,
    slaBreachRisk: 0,
    escalationTrends: {
      today: 0,
      yesterday: 0,
      thisWeek: 0,
      lastWeek: 0
    }
  });
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const { data: priorities = [] } = usePriorities();
  const { data: statuses = [] } = useStatuses();
  const [inquiryPriorityLevels, setInquiryPriorityLevels] = useState([]);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const skipInitialTableRefreshRef = useRef(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await ticketService.getInquiryPriorityLevelsForCreate();
        if (!cancelled) setInquiryPriorityLevels(Array.isArray(rows) ? rows : []);
      } catch (e) {
        console.warn('Failed to load inquiry priority levels for admin escalated filters', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const getPriorityFilterValueByName = React.useCallback((priorityName) => {
    const normalizedName = (priorityName || '').toLowerCase();
    const inquiryMatch = inquiryPriorityLevels.find((p) => {
      const name = (p.priorityLevelName || p.name || '').toLowerCase();
      return name === normalizedName || name.includes(normalizedName);
    });
    if (inquiryMatch) {
      return String(inquiryMatch.id || inquiryMatch.inquiryPriorityLevelId);
    }
    const match = priorities.find((p) => {
      const code = (p.priorityCode || '').toLowerCase();
      const name = (p.priorityName || '').toLowerCase();
      return code === normalizedName || name === normalizedName;
    });
    return match?.priorityId ? String(match.priorityId) : null;
  }, [priorities, inquiryPriorityLevels]);

  const isCriticalFilterActive = React.useMemo(() => {
    if (priorityFilter === 'all') return false;
    const criticalFilterValue = getPriorityFilterValueByName('critical');
    if (!criticalFilterValue) return false;
    return String(priorityFilter) === String(criticalFilterValue);
  }, [priorityFilter, getPriorityFilterValueByName]);

  const handleQuickFilterTotalEscalated = () => {
    setPriorityFilter('all');
  };

  const handleQuickFilterCritical = () => {
    const criticalFilterValue = getPriorityFilterValueByName('critical');
    if (criticalFilterValue) {
      setPriorityFilter(criticalFilterValue);
      return;
    }
    toast.error('Critical priority is not available yet.');
  };

  // Fetch function for ALL escalated tickets (admin view) - uses dedicated escalated endpoint
  // Use useCallback to ensure it always has the latest filter values
  const fetchAllEscalatedTickets = React.useCallback(async (params = {}) => {
    // Build filter parameters
    const fetchParams = {
      page: params.page || 0,
      size: params.size || 10
    };
    
    // Add search parameter
    if (searchTerm && searchTerm.trim()) {
      fetchParams.search = searchTerm.trim();
    }
    
    // Add priority filter — Inquiry Priority Level id (BE OR-matches inquiry_priority_level_id)
    if (priorityFilter !== 'all' && priorityFilter) {
      const parsed = parseInt(priorityFilter, 10);
      if (Number.isFinite(parsed)) {
        fetchParams.priorityId = parsed;
      }
    }
    
    // Add date filters - convert to ISO format for backend (LocalDateTime format: YYYY-MM-DDTHH:mm:ss)
    if (startDate) {
      // Convert date string to LocalDateTime format (YYYY-MM-DDTHH:mm:ss) without timezone
      const startDateTime = new Date(startDate);
      startDateTime.setHours(0, 0, 0, 0); // Set to start of day
      const year = startDateTime.getFullYear();
      const month = String(startDateTime.getMonth() + 1).padStart(2, '0');
      const day = String(startDateTime.getDate()).padStart(2, '0');
      fetchParams.startDate = `${year}-${month}-${day}T00:00:00`;
    }
    if (endDate) {
      // Convert date string to LocalDateTime format (YYYY-MM-DDTHH:mm:ss) without timezone
      const endDateTime = new Date(endDate);
      endDateTime.setHours(23, 59, 59, 999); // Set to end of day
      const year = endDateTime.getFullYear();
      const month = String(endDateTime.getMonth() + 1).padStart(2, '0');
      const day = String(endDateTime.getDate()).padStart(2, '0');
      fetchParams.endDate = `${year}-${month}-${day}T23:59:59`;
    }
    
    // Backend endpoint: GET /api/ticket/escalated
    // The backend automatically returns all escalated tickets for admins (roleId = 2)
    return await ticketService.getEscalatedTickets(fetchParams);
  }, [searchTerm, priorityFilter, startDate, endDate, priorities]);

  const emptyEscalationStats = () => ({
    totalEscalated: 0,
    criticalEscalated: 0,
    highEscalated: 0,
    mediumEscalated: 0,
    lowEscalated: 0,
    avgEscalationTime: '0h 0m',
    escalationRate: 0,
    slaBreachRisk: 0,
    escalationTrends: {
      today: 0,
      yesterday: 0,
      thisWeek: 0,
      lastWeek: 0
    }
  });

  const applyEscalatedStatsFromApi = (stats) => {
    if (!stats) {
      setEscalationStats(emptyEscalationStats());
      return;
    }
    const avgMinutes = stats.avgEscalationMinutes ?? 0;
    const hours = Math.floor(avgMinutes / 60);
    const minutes = Math.floor(avgMinutes % 60);
    const trends = stats.escalationTrends || {};
    setEscalationStats({
      totalEscalated: stats.totalEscalated || 0,
      criticalEscalated: stats.criticalEscalated || 0,
      highEscalated: stats.highEscalated || 0,
      mediumEscalated: stats.mediumEscalated || 0,
      lowEscalated: stats.lowEscalated || 0,
      avgEscalationTime: `${hours}h ${minutes}m`,
      escalationRate: 0,
      slaBreachRisk: stats.slaBreachRisk || 0,
      escalationTrends: {
        today: trends.today || 0,
        yesterday: trends.yesterday || 0,
        thisWeek: trends.thisWeek || 0,
        lastWeek: trends.lastWeek || 0
      }
    });
  };

  useEffect(() => {
    const loadStatistics = async () => {
      try {
        setLoading(true);
        const response = await ticketService.getEscalatedStatistics();
        const responseData = response?.data || response;
        if (responseData?.success && responseData?.data) {
          applyEscalatedStatsFromApi(responseData.data);
        } else {
          setEscalationStats(emptyEscalationStats());
        }
      } catch (err) {
        console.error('Failed to load statistics:', err);
        setEscalationStats(emptyEscalationStats());
      } finally {
        setLoading(false);
      }
    };

    loadStatistics();
  }, []);

  // Calculate escalation statistics from tickets (export / fallback only)
  const calculateEscalationStats = (tickets) => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(yesterdayStart.getDate() - 1);
    const thisWeekStart = new Date(todayStart);
    thisWeekStart.setDate(todayStart.getDate() - todayStart.getDay());
    const lastWeekStart = new Date(thisWeekStart);
    lastWeekStart.setDate(lastWeekStart.getDate() - 7);

    let criticalCount = 0;
    let highCount = 0;
    let mediumCount = 0;
    let lowCount = 0;
    let todayCount = 0;
    let yesterdayCount = 0;
    let thisWeekCount = 0;
    let lastWeekCount = 0;
    let totalEscalationMinutes = 0;
    let escalationCount = 0;
    let slaBreachRisk = 0;

    tickets.forEach(ticket => {
      // Priority counts
      if (ticket.priority === 'critical') criticalCount++;
      else if (ticket.priority === 'high') highCount++;
      else if (ticket.priority === 'medium') mediumCount++;
      else if (ticket.priority === 'low') lowCount++;

      // Escalation trends
      const escalatedDate = new Date(ticket.escalationDate || ticket.updatedAt);
      if (escalatedDate >= todayStart) todayCount++;
      else if (escalatedDate >= yesterdayStart && escalatedDate < todayStart) yesterdayCount++;
      
      if (escalatedDate >= thisWeekStart) thisWeekCount++;
      else if (escalatedDate >= lastWeekStart && escalatedDate < thisWeekStart) lastWeekCount++;

      // Average escalation time
      if (ticket.createdAt && ticket.escalationDate) {
        const created = new Date(ticket.createdAt);
        const escalated = new Date(ticket.escalationDate);
        const diffMinutes = Math.floor((escalated - created) / (1000 * 60));
        if (diffMinutes > 0) {
          totalEscalationMinutes += diffMinutes;
          escalationCount++;
        }
      }

      // SLA breach risk
      if (ticket.slaIsOverdue || ticket.slaStatus === 'breached') {
        slaBreachRisk++;
      }
    });

    let avgEscalationTime = '0h 0m';
    if (escalationCount > 0) {
      const avgMinutes = totalEscalationMinutes / escalationCount;
      const hours = Math.floor(avgMinutes / 60);
      const minutes = Math.floor(avgMinutes % 60);
      avgEscalationTime = `${hours}h ${minutes}m`;
    }

    setEscalationStats({
      totalEscalated: tickets.length,
      criticalEscalated: criticalCount,
      highEscalated: highCount,
      mediumEscalated: mediumCount,
      lowEscalated: lowCount,
      avgEscalationTime: avgEscalationTime,
      escalationRate: 0,
      slaBreachRisk: slaBreachRisk,
      escalationTrends: {
        today: todayCount,
        yesterday: yesterdayCount,
        thisWeek: thisWeekCount,
        lastWeek: lastWeekCount
      }
    });
  };

  const handleRefresh = async () => {
    try {
      setLoading(true);
      const response = await ticketService.getEscalatedStatistics();
      const responseData = response?.data || response;
      if (responseData?.success && responseData?.data) {
        applyEscalatedStatsFromApi(responseData.data);
      }
    } catch (err) {
      console.error('Failed to refresh statistics:', err);
    } finally {
      setLoading(false);
    }
    setRefreshKey(prev => prev + 1);
  };

  const formatDateTimeExport = (isoString) => {
    if (!isoString) return '-';
    try {
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return '-';
      return d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch {
      return '-';
    }
  };

  const escapeCsvCell = (val) => {
    if (val == null || val === '') return '';
    const s = String(val);
    if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const fetchEscalatedForExport = async () => {
    const response = await fetchAllEscalatedTickets({ page: 0, size: 1000 });
    const content = response?.data?.data?.content || [];
    return (content || []).map((ticket) => {
      const sla = resolveCurrentSlaWindow(ticket, 0, statuses);
      return {
        ticketNumber: ticket.ticketNumber || ticket.ticketId || 'N/A',
        subject: ticket.subject || '',
        inquiryType: ticket.inquiryTypeName || '—',
        inquiryCategory: resolveTicketCategoryLabel(ticket),
        inquirySubCategory: ticket.inquirySubCategoryName || '—',
        inquiryPriority: resolveTicketPriorityLabel(ticket),
        responsibleRole: ticket.responsibleRoleName || '—',
        escalationLevel: ticket.escalationLevelName || '—',
        assignee: ticket.assigneeFullName || (ticket.assigneeId ? `Agent ${ticket.assigneeId}` : 'Unassigned'),
        priorityName: resolveTicketPriorityLabel(ticket),
        status: (ticket.status || 'open').toLowerCase(),
        slaStatus: sla?.label || '—',
        escalatedDate: ticket.escalatedAt || ticket.escalatedDate || ticket.updatedAt || ticket.createdAt
      };
    });
  };

  const handleExportCsv = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const rows = await fetchEscalatedForExport();
      if (rows.length === 0) {
        toast.error('No tickets to export');
        return;
      }
      const headers = ['S.NO', 'Ticket Number', 'Subject', 'Inquiry Type', 'Inquiry Category', 'Inquiry Sub Category', 'Inquiry Priority', 'Responsible Role', 'Escalation Level', 'Assignee', 'Status', 'SLA', 'Escalated Date'];
      const csvRows = rows.map((r, i) => [
        i + 1, r.ticketNumber, r.subject, r.inquiryType, r.inquiryCategory, r.inquirySubCategory, r.inquiryPriority, r.responsibleRole, r.escalationLevel, r.assignee, r.status, r.slaStatus, formatDateTimeExport(r.escalatedDate)
      ].map(escapeCsvCell));
      const csv = [headers.map(escapeCsvCell).join(','), ...csvRows.map((r) => r.join(','))].join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `escalated-tickets-management-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Tickets exported as CSV');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportMenuOpen(false);
    setExporting(true);
    try {
      const rows = await fetchEscalatedForExport();
      if (rows.length === 0) {
        toast.error('No tickets to export');
        return;
      }
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(14);
      doc.text('Escalated Tickets Management', 14, 15);
      doc.setFontSize(10);
      const body = rows.map((r, i) => [
        i + 1, r.ticketNumber, (r.subject || '').substring(0, 24), (r.inquiryCategory || '').substring(0, 12), (r.inquiryPriority || '').substring(0, 10), (r.escalationLevel || '').substring(0, 10), (r.assignee || '').substring(0, 12), r.status, (r.slaStatus || '').substring(0, 12), formatDateTimeExport(r.escalatedDate)
      ]);
      autoTable(doc, {
        startY: 22,
        head: [['S.NO', 'Ticket', 'Subject', 'Category', 'Priority', 'Level', 'Assignee', 'Status', 'SLA', 'Escalated']],
        body,
        styles: { fontSize: 7 }
      });
      doc.save(`escalated-tickets-management-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('Tickets exported as PDF');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  // Trigger table refresh when filters change (priority, dates) — not on initial mount
  // Note: searchTerm is handled by EscalatedTicketsTable's own effect with debouncing
  useEffect(() => {
    if (skipInitialTableRefreshRef.current) {
      skipInitialTableRefreshRef.current = false;
      return;
    }
    setRefreshKey((prev) => prev + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priorityFilter, startDate, endDate]);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <FileText className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              Escalated Tickets Management
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            Monitor and manage escalated tickets that require immediate attention
          </p>
        </div>
        <div className="flex items-center gap-2 flex-nowrap">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <div className="relative flex-shrink-0">
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={(e) => {
                e.stopPropagation();
                setExportMenuOpen((v) => !v);
              }}
              disabled={loading || exporting}
            >
              <Download className="h-4 w-4" />
              <span>{exporting ? 'Exporting…' : 'Export'}</span>
              <ChevronDown className="h-4 w-4" />
            </Button>
            {exportMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" aria-hidden onClick={() => setExportMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 py-1 z-50 min-w-[140px] rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                    onClick={handleExportCsv}
                  >
                    <FileText className="h-4 w-4" />
                    Export CSV
                  </button>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                    onClick={handleExportPdf}
                  >
                    <FileText className="h-4 w-4" />
                    Export PDF
                  </button>
                </div>
              </>
            )}
          </div>
          <Button
            variant="outline"
            className="flex items-center gap-2"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4" />
            Filters
          </Button>
        </div>
      </div>

      {/* Search Bar - Always visible above the table */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search tickets by subject, description, or ticket number..."
              value={searchTerm}
              onChange={(e) => {
                e.preventDefault();
                setSearchTerm(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              className="pl-10 pr-4 py-2.5 w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </CardContent>
      </Card>

      {/* Filters Section */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 flex-wrap">
              {/* Inquiry Priority Dropdown */}
              <div className="relative flex-shrink-0">
                <select
                  value={priorityFilter}
                  onChange={(e) => {
                    e.preventDefault();
                    setPriorityFilter(e.target.value);
                  }}
                  className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 min-w-[140px]"
                >
                  <option value="all">All Inquiry Priorities</option>
                  {inquiryPriorityLevels.map((priority) => (
                    <option
                      key={priority.id || priority.inquiryPriorityLevelId}
                      value={String(priority.id || priority.inquiryPriorityLevelId)}
                    >
                      {priority.priorityLevelName || priority.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Start Date */}
              <div className="relative flex-shrink-0">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    e.preventDefault();
                    setStartDate(e.target.value);
                  }}
                  className="pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-w-[160px]"
                  placeholder="Start Date"
                />
              </div>

              {/* End Date */}
              <div className="relative flex-shrink-0">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    e.preventDefault();
                    setEndDate(e.target.value);
                  }}
                  className="pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-w-[160px]"
                  placeholder="End Date"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    setSearchTerm('');
                    setPriorityFilter('all');
                    setStartDate('');
                    setEndDate('');
                  }}
                  className="text-sm px-4 py-2 whitespace-nowrap"
                >
                  Clear All
                </Button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    setShowFilters(false);
                  }}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reusable Escalated Tickets Table */}
      <EscalatedTicketsTable
        fetchFunction={fetchAllEscalatedTickets}
        viewTicketRoute="/admin/tickets/:id"
        emptyStateMessage="All tickets are being handled within SLA"
        refreshTrigger={refreshKey}
        searchTerm={searchTerm}
        externalPriorities={priorities}
      />
    </div>
  );
};

export default EscalatedTicketsManagement;

